/**
 * Kosh API service — thin typed wrappers over the runtime's /api/v1/kosh/*
 * endpoints (and the core /customers endpoint Kosh references by id). The
 * shared apiClient already unwraps the runtime's { success, data } envelope,
 * so these return plain payloads.
 */
import { apiClient } from './api-client';
import type {
  CoreCustomer,
  KoshAccount,
  KoshCollectionActivity,
  KoshDashboard,
  KoshInvoice,
  KoshInvoiceStatus,
  KoshOutstanding,
  KoshPackagingProfile,
  KoshReminder,
  KoshSettlement,
  Paginated,
  VerticalPreset,
} from '@/types/kosh';

type Query = Record<string, string | number | boolean | undefined>;

// The list endpoints return { items, page, pageSize, total }. A couple of
// resources may return a bare array; normalize both to T[].
function rows<T>(data: Paginated<T> | T[] | null | undefined): T[] {
  if (!data) return [];
  return Array.isArray(data) ? data : data.items;
}

async function getList<T>(path: string, params?: Query): Promise<T[]> {
  const { data } = await apiClient.get<Paginated<T> | T[]>(path, { params });
  return rows<T>(data);
}

// ── Packaging (business template) ───────────────────────────────────────────
export const packagingApi = {
  presets: () => apiClient.get<VerticalPreset[]>('/kosh/packaging/presets').then((r) => r.data),
  profiles: () => getList<KoshPackagingProfile>('/kosh/packaging/profiles'),
  createProfile: (body: Partial<KoshPackagingProfile>) =>
    apiClient.post<KoshPackagingProfile>('/kosh/packaging/profiles', body).then((r) => r.data),
  updateProfile: (id: string, body: Partial<KoshPackagingProfile>) =>
    apiClient.patch<KoshPackagingProfile>(`/kosh/packaging/profiles/${id}`, body).then((r) => r.data),
};

// ── Commercial ──────────────────────────────────────────────────────────────
export const accountsApi = {
  list: (params?: Query) => getList<KoshAccount>('/kosh/accounts', params),
  get: (id: string) => apiClient.get<KoshAccount>(`/kosh/accounts/${id}`).then((r) => r.data),
  customers: (id: string) =>
    getList<CoreCustomer>(`/kosh/accounts/${id}/customers`),
};

export const customersApi = {
  // Core operations customers — Kosh references these by scalar id (ADR-010).
  list: (params?: Query) => getList<CoreCustomer>('/customers', params),
};

// ── Billing ───────────────────────────────────────────────────────────────
export const invoicesApi = {
  list: (params?: Query) => getList<KoshInvoice>('/kosh/invoices', params),
  get: (id: string) => apiClient.get<KoshInvoice>(`/kosh/invoices/${id}`).then((r) => r.data),
  transition: (id: string, to: KoshInvoiceStatus) =>
    apiClient.post<KoshInvoice>(`/kosh/invoices/${id}/transitions`, { to }).then((r) => r.data),
};

// ── Receivables ─────────────────────────────────────────────────────────────
export const outstandingsApi = {
  list: (params?: Query) => getList<KoshOutstanding>('/kosh/outstandings', params),
  get: (id: string) =>
    apiClient.get<KoshOutstanding>(`/kosh/outstandings/${id}`).then((r) => r.data),
  recompute: (id: string) =>
    apiClient.post<KoshOutstanding>(`/kosh/outstandings/${id}/recompute`, {}).then((r) => r.data),
  writeOff: (id: string, reason?: string) =>
    apiClient
      .post<KoshOutstanding>(`/kosh/outstandings/${id}/write-off`, { reason })
      .then((r) => r.data),
  dispute: (id: string, inDispute: boolean) =>
    apiClient
      .post<KoshOutstanding>(`/kosh/outstandings/${id}/dispute`, { inDispute })
      .then((r) => r.data),
};

export const settlementsApi = {
  list: (params?: Query) => getList<KoshSettlement>('/kosh/settlements', params),
  create: (body: Partial<KoshSettlement> & { outstandingId: string; amount: number }) =>
    apiClient.post<KoshSettlement>('/kosh/settlements', body).then((r) => r.data),
  confirm: (id: string) =>
    apiClient.post<KoshSettlement>(`/kosh/settlements/${id}/confirm`, {}).then((r) => r.data),
  reverse: (id: string) =>
    apiClient.post<KoshSettlement>(`/kosh/settlements/${id}/reverse`, {}).then((r) => r.data),
};

export const collectionsApi = {
  activities: (params?: Query) =>
    getList<KoshCollectionActivity>('/kosh/collection-activities', params),
  createActivity: (
    body: Partial<KoshCollectionActivity> & { outstandingId: string; channel: string },
  ) =>
    apiClient
      .post<KoshCollectionActivity>('/kosh/collection-activities', body)
      .then((r) => r.data),
  completeActivity: (id: string) =>
    apiClient
      .post<KoshCollectionActivity>(`/kosh/collection-activities/${id}/complete`, {})
      .then((r) => r.data),
  reminders: (params?: Query) => getList<KoshReminder>('/kosh/reminders', params),
};

// ── Visibility ──────────────────────────────────────────────────────────────
export const visibilityApi = {
  dashboard: (params?: Query) =>
    apiClient.get<KoshDashboard>('/kosh/visibility/dashboard', { params }).then((r) => r.data),
};
